import { Reveal, RevealLines } from "./Reveal";
import { useLocale } from "@/i18n/LocaleContext";

// ============================================================
// "The Man Behind the Machine" — COMPLETE Founder chapter.
// Reproduced from the institutional YANRE FITNESS EUROPE WEBSITE.
// Official George Yang portraits: /founder-hero.png, /founder-card.png.
// Copy is the approved English master; dictionaries override per locale.
// ============================================================

const STORY = [
  { num: "01", key: "fo.st1", title: "The Beginning", body: "In 1991, George Yang was not a founder — he was a technician, repairing gym machines for China's National Athletes Team. Repairing revealed the truth; he trained on the machines himself to feel exactly where they failed." },
  { num: "02", key: "fo.st2", title: "The Problem", body: "Even the world's “best” brands carried flaws — weak welds, poor angles, and designs that ignored the human body. The limitations he saw shaped an entirely different way of thinking about equipment.", accent: true },
  { num: "03", key: "fo.st3", title: "The Foundation", body: "In 1997 he stopped fixing other people's mistakes and set his own standard — founding Yangrui Fitness in Beijing, named after his son. The name evolved from Yangrui to Yanre to YR; the standard never changed." },
  { num: "04", key: "fo.st4", title: "The Engineer", body: "He refuses to compromise on materials: Q235 qualified steel at a 3 mm minimum, 30CrMnSiA barbells, low-friction POM cams and pulleys, and Japan-imported cables rated to 1400kg. “I train on these machines myself.”" },
  { num: "05", key: "fo.st5", title: "The Designer", body: "His philosophy is clean and minimalist — design is not how it looks, but how it works. YANRE engineers rather than copies; the 73 Series was the first in the world to use independent, converging and diverging movement paths." },
  { num: "06", key: "fo.st6", title: "The Vision", body: "A factory of craftsmen, not a warehouse of boxes — a minimum order of one, real customization, and support built for a gym's 10th year, not just its first day. A partner, not a boss." },
];

const MILESTONES = [
  { year: "1991", key: "fo.m1", title: "Early Technical Experience", body: "Technician for China's National Athletes Team." },
  { year: "1997", key: "fo.m2", title: "Founding of YANRE", body: "Yangrui Fitness established in Beijing." },
  { year: "2006", key: "fo.m3", title: "The 73 Series", body: "World-first independent, converging & diverging paths." },
];

const PHILOSOPHY = [
  { key: "fo.c1", title: "Materials", body: "Q235 steel, 3mm minimum. POM cams. 1400kg cables." },
  { key: "fo.c2", title: "Biomechanics", body: "Movement paths engineered around the human body." },
  { key: "fo.c3", title: "Durability", body: "Built to last 10+ years of professional use." },
  { key: "fo.c4", title: "Standard", body: "ISO 20957 as a starting point — never the ceiling." },
];

const ChapterLabel = ({ children, testId }) => {
  const { t } = useLocale();
  return (
    <div className="flex items-center gap-4" data-testid={testId}>
      <span className="h-px w-10 bg-signal" />
      <span className="overline">{t(`label.${children}`, children)}</span>
    </div>
  );
};

export const Founder = ({
  index,
  ghostYear = "1997",
  portraitSrc = "/founder-hero.png",
  portraitCardSrc = "/founder-card.png",
}) => {
  const { t } = useLocale();
  return (
    <div className="section-darker relative border-t border-carbon-700" data-testid="founder-section">
      {/* ============ PART A — FOUNDER HERO ============ */}
      <section className="relative overflow-hidden">
        <div className="container-edge relative">
          <Reveal>
            <div
              className="flex items-center justify-between border-b border-carbon-800 py-4 font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/40"
              data-testid="founder-subnav"
            >
              <span>{t("fo.navFactory", "The Factory")}</span>
              <span className="flex items-center gap-2 text-titanium/60">
                <span className="text-signal">→</span> {t("fo.navFounder", "The Man Behind the Machine")}
              </span>
            </div>
          </Reveal>
          <div className="grid items-end gap-10 py-14 lg:grid-cols-2 lg:gap-8 lg:py-20">
            <div className="relative z-10">
              <Reveal>
                <ChapterLabel testId="founder-label">
                  {index && <span className="mr-3 text-titanium/40 rtl:ml-3 rtl:mr-0">{index}</span>}
                  The Founder
                </ChapterLabel>
              </Reveal>
              <h2 className="mt-10 font-display text-6xl font-extrabold uppercase leading-[0.88] tracking-tight text-white sm:text-7xl lg:text-8xl">
                <RevealLines
                  lines={[
                    t("fo.h1a", "The Man"),
                    t("fo.h1b", "Behind"),
                    <span key="m">
                      {t("fo.h1c", "The Machine.")}
                    </span>,
                  ]}
                />
              </h2>
              <Reveal delay={0.15}>
                <p className="mt-10 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <span className="font-display text-2xl font-bold uppercase text-white sm:text-3xl">George Yang</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal sm:text-[11px]">
                    {t("fo.role", "Founder & Chief Designer")}
                  </span>
                </p>
                <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-titanium/60 sm:text-lg">
                  {t("fo.body", "An athlete, an engineer and the founder of YANRE. Since 1997, he has built machines for those who value engineering over marketing.")}
                </p>
                {/* Quote — desktop: under the copy (reference composition) */}
                <blockquote className="mt-8 hidden max-w-lg font-display text-xl font-bold italic leading-snug text-white sm:text-2xl lg:block">
                  {t("fo.quote", "“To understand the machine, you must understand the man behind it.”")}
                </blockquote>
              </Reveal>
            </div>

            {/* Portrait composition — ghost 1997 is contained here and can
                never escape upward into the chapter navigation. */}
            <Reveal delay={0.2} className="relative">
              <div className="relative overflow-hidden">
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute right-0 top-4 z-0 select-none font-display text-[9rem] font-extrabold leading-none tracking-tight text-carbon-800 sm:top-8 sm:text-[12rem] lg:text-[15rem]"
                  data-testid="founder-ghost-year"
                >
                  {ghostYear}
                </span>
                <img
                  src={portraitSrc}
                  alt="George Yang — Founder & Chief Designer of YANRE FITNESS"
                  loading="lazy"
                  className="relative z-10 mx-auto mt-14 w-full max-w-sm sm:mt-16 sm:max-w-md lg:ml-auto lg:mr-0 lg:mt-20 lg:max-w-lg"
                  data-testid="founder-portrait"
                />
              </div>
              {/* Quote — mobile/tablet: after the portrait (stacked adaptation) */}
              <blockquote
                aria-hidden="true"
                className="mt-8 max-w-lg font-display text-xl font-bold italic leading-snug text-white sm:text-2xl lg:hidden"
              >
                {t("fo.quote", "“To understand the machine, you must understand the man behind it.”")}
              </blockquote>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ PART B — SIX-PART FOUNDER STORY ============ */}
      <section className="relative border-t border-carbon-800" data-testid="founder-story">
        <span aria-hidden="true" className="absolute right-[16%] top-0 hidden h-14 w-px bg-signal lg:block rtl:left-[16%] rtl:right-auto" />
        <div className="container-edge relative py-14 lg:py-20">
          <div className="grid gap-x-12 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-testid="founder-story-grid">
            {STORY.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.05}>
                <div className="border-t border-carbon-700 pt-8">
                  <div className="flex items-center gap-3">
                    <span className={`font-display text-3xl font-extrabold ${s.accent ? "text-signal" : "text-carbon-500"}`}>
                      {s.num}
                    </span>
                    <span className="h-px w-5 bg-signal" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold uppercase text-white sm:text-2xl">
                    {t(`${s.key}.t`, s.title)}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-titanium/60 sm:text-base">{t(`${s.key}.b`, s.body)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PART C — MILESTONES ============ */}
      <section className="relative border-t border-carbon-800" data-testid="founder-milestones">
        <div className="container-edge relative py-14 lg:py-20">
          <Reveal>
            <ChapterLabel testId="founder-milestones-label">Milestones</ChapterLabel>
          </Reveal>
          <div className="mt-10 grid gap-px sm:grid-cols-3" data-testid="milestones-grid">
            {MILESTONES.map((m, i) => (
              <Reveal key={m.year} delay={i * 0.08}>
                <div className="h-full border border-carbon-700 bg-carbon-900 p-7 sm:p-9">
                  <p className="flex items-center gap-3">
                    <span className="h-2 w-2 bg-signal" />
                    <span className="font-display text-4xl font-extrabold text-white sm:text-5xl">{m.year}</span>
                  </p>
                  <h3 className="mt-5 font-display text-lg font-bold uppercase text-white sm:text-xl">{t(`${m.key}.t`, m.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-titanium/60">{t(`${m.key}.b`, m.body)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PART D — ENGINEERING PHILOSOPHY ============ */}
      <section className="relative border-t border-carbon-800" data-testid="founder-philosophy">
        <div className="container-edge relative py-14 lg:py-20">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div className="relative aspect-[4/3] w-full overflow-hidden border border-carbon-700 bg-carbon-900" data-testid="founder-portrait-card">
                <div className="grid-lines absolute inset-0 opacity-40" />
                <img
                  src={portraitCardSrc}
                  alt="George Yang — Founder & Chief Designer of YANRE FITNESS"
                  loading="lazy"
                  className="absolute inset-x-0 bottom-0 mx-auto h-full w-auto max-w-none object-contain object-bottom"
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-carbon-950/90 to-transparent" />
                <span className="absolute bottom-4 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/60 rtl:left-auto rtl:right-5">
                  George Yang — {t("fo.role", "Founder & Chief Designer")}
                </span>
              </div>
            </Reveal>
            <div>
              <Reveal>
                <ChapterLabel testId="founder-philosophy-label">Engineering Philosophy</ChapterLabel>
              </Reveal>
              <Reveal delay={0.1}>
                <blockquote className="mt-8 font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
                  {t("fo.pqA", "“We make products with")} <span className="text-signal">{t("fo.pqHeart", "heart")}</span>{t("fo.pqB", ", and we make products with")} <span className="text-signal">{t("fo.pqStandard", "standard")}</span><span className="text-signal">{t("fo.pqEnd", ".”")}</span>
                </blockquote>
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/50 sm:text-[11px]">
                  — George Yang, {t("fo.role", "Founder & Chief Designer")}
                </p>
              </Reveal>
              <div className="mt-10 grid gap-px border border-carbon-700 sm:grid-cols-2" data-testid="philosophy-grid">
                {PHILOSOPHY.map((p, i) => (
                  <Reveal key={p.key} delay={i * 0.05}>
                    <div className="h-full bg-carbon-900 p-6 sm:p-7">
                      <h4 className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal sm:text-[11px]">
                        {t(`${p.key}.t`, p.title)}
                      </h4>
                      <p className="mt-3 text-sm leading-relaxed text-titanium/60">{t(`${p.key}.b`, p.body)}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
